import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeliveryWizard from './DeliveryWizard';
import ExchangeWizard from './ExchangeWizard';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '../../config';
import { normalizePhoneCd, buildWaLink } from '../../utils/whatsapp';
import { NdoeImage , messageErreur } from '../../components/ndoe';
import { draftKey, readDraft, writeDraft, clearDraft, mergeLinesWithDraft, draftHasContent } from '../../utils/formDraft';
import usePersistentState from '../../hooks/usePersistentState';
import { subscribeRealtime } from '../../realtime';
import './mobileDeliveryHub.css';

// Les 5 issues de livraison (mêmes valeurs serveur) et leur philosophie « le
// livreur ne DÉCIDE plus d'un échange » sont désormais portées par le wizard
// (OUTCOME_BTN dans DeliveryWizard.jsx) qui a remplacé la saisie à plat.

const localDateTime = () => {
  const d = new Date();
  const p = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// La vignette de ligne (économie de données) est désormais rendue par NdoeImage
// dans le wizard, qui sert le dérivé WebP adapté avec repli sur l'original.

const money = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} $`;

const statusLabel = (status) => ({
  AFFECTEE: 'À prendre',
  PRISE_EN_CHARGE: 'Prise en charge',
  EN_COURS: 'En cours',
  REPORTEE: 'Reportée',
  NON_PRISE_EN_CHARGE: 'Non prise',
  EN_ESSAI: 'En essai',
  LIVREE: 'Livrée',
  RETOUR_LIVRAISON: 'Retour magasin',
  RETOUR_CLIENT: 'Retour client',
  RETOUR_POUR_ECHANGE: 'Retour pour échange',
  ECHANGE_TERMINE: 'Échange terminé',
  ECHANGE_RETOURNE: 'Échange retourné',
}[status] || status);

// Ton visuel des statuts figés — même sémantique que les couleurs du select de
// résultat (aligné sur LIVRAISON_STATUS de src/components/ndoe/NdoeStatusBadge).
const statusTone = (status) => ({
  LIVREE: 'ok',
  ECHANGE_TERMINE: 'ok',
  EN_ESSAI: 'info',
  AFFECTEE: 'info',
  PRISE_EN_CHARGE: 'info',
  EN_COURS: 'info',
  REPORTEE: 'gold',
  RETOUR_LIVRAISON: 'danger',
  RETOUR_CLIENT: 'danger',
  ECHANGE_RETOURNE: 'danger',
  RETOUR_POUR_ECHANGE: 'warn',
  NON_PRISE_EN_CHARGE: 'warn',
}[status] || '');
const toneClass = (status) => (statusTone(status) ? ` is-tone-${statusTone(status)}` : '');

// Progression du lot d'une commande : « 2/6 paires livrées » dès qu'au moins une
// paire est confirmée livrée, sinon l'affichage historique « 6 paire(s) ».
// paires_lot / paires_livrees sont additifs côté API — absents (vieille API), on
// retombe sur nombre_paires.
const pairsProgress = (o) => {
  const livrees = Number(o?.paires_livrees || 0);
  const lot = Number(o?.paires_lot || 0) || Number(o?.nombre_paires || 0);
  if (livrees > 0 && lot > 0) {
    return { text: `${livrees}/${lot} paire(s) livrée(s)`, cls: livrees >= lot ? ' is-complete' : ' is-partial' };
  }
  return { text: `${Number(o?.nombre_paires || 0)} paire(s)`, cls: '' };
};

export default function MobileDeliveryHub() {
  const domain = config.REACT_APP_SERVER_DOMAIN;
  const user = useSelector((state) => state.user.currentUser);
  const token = user?.accessToken || user?.token || '';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [scope, setScope] = useState('take-charge');
  const [orders, setOrders] = useState([]);
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [effectiveAt, setEffectiveAt] = useState(localDateTime());
  const [comment, setComment] = useState('');
  const [payment, setPayment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Confirmation IN-APP : window.confirm est supprimé dans les navigateurs mobiles
  // in-app (le bouton semblait « ne rien faire »). { message, onConfirm }.
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  // Ouverture d'une commande pilotée par l'URL (?commande=…) : le bouton retour
  // du navigateur revient à la liste au lieu de sortir du hub.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const commandeParam = searchParams.get('commande');
  // Deep-link du bandeau « à finaliser » : ?scope=active ouvre directement
  // l'onglet « En circulation ». Le paramètre est consommé (retiré de l'URL) pour
  // ne pas rester collé à ce scope après un retour arrière.
  useEffect(() => {
    const s = searchParams.get('scope');
    if (s && ['take-charge', 'active', 'echanges', 'closed'].includes(s)) {
      setScope(s);
      const next = new URLSearchParams(searchParams);
      next.delete('scope');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Supervision admin : opérer les livraisons d'un livreur choisi + corriger.
  const isAdmin = user?.role === 'admin';
  const [livreurs, setLivreurs] = useState([]);
  const [erreurLivreurs, setErreurLivreurs] = useState(null);
  // Le livreur supervisé par l'admin est persisté (retrouvé après un reload).
  const [asLivreur, setAsLivreur] = usePersistentState('ndoe:delivery:asLivreur', '');
  const [reassignTo, setReassignTo] = useState('');
  const [correcting, setCorrecting] = useState(null);
  const [correctForm, setCorrectForm] = useState({ prix: '', qte_livre: '', statut: '' });
  // Réactivation (Option B) : config SLA publiée par l'API + liste des livraisons
  // en retard (supervision admin) + id en cours de réactivation.
  const [sla, setSla] = useState({ jours: 3, relance_heures: 48 });
  const [correctionH, setCorrectionH] = useState(3);       // fenêtre d'auto-correction prix livreur
  const [priceEdit, setPriceEdit] = useState(null);        // { id, prix } — correction prix en cours
  // Images de ligne repliées par défaut (anti-encombrement) : révélées au clic
  // sur « Afficher » (id_detail_livraison -> bool).
  const [imgVisible, setImgVisible] = useState({});
  const toggleImg = (id) => setImgVisible((m) => ({ ...m, [id]: !m[id] }));
  // Accusé de réception affiché APRÈS enregistrement (récap relu en base par le
  // serveur). Tant qu'il est posé, il remplace le wizard : le livreur ferme lui-même.
  const [confirmation, setConfirmation] = useState(null);
  const [lateList, setLateList] = useState([]);
  const [reactivatingId, setReactivatingId] = useState(null);

  // Paramètre id_livreur : ajouté uniquement pour un admin ayant choisi un
  // livreur. Le serveur ignore/refuse ce paramètre pour un non-admin.
  const targetParam = useCallback(
    () => (isAdmin && asLivreur ? { id_livreur: Number(asLivreur) } : {}),
    [isAdmin, asLivreur],
  );

  const loadOrders = useCallback(async () => {
    // Admin sans livreur choisi : rien à afficher (fail-safe).
    if (isAdmin && !asLivreur) { setOrders([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { scope, ...(isAdmin && asLivreur ? { id_livreur: Number(asLivreur) } : {}) };
      const response = await axios.get(`${domain}/api/mobile-delivery/mine`, { params, headers });
      setOrders(response.data.orders || []);
      if (response.data.sla) setSla(response.data.sla);
      if (response.data.correction_livreur_heures != null) setCorrectionH(Number(response.data.correction_livreur_heures));
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger vos livraisons.');
    } finally {
      setLoading(false);
    }
  }, [domain, headers, scope, isAdmin, asLivreur]);

  useEffect(() => { if (scope !== 'en-retard') loadOrders(); }, [loadOrders, scope]);

  // Échanges à livrer (Cas B) : la nouvelle paire à remettre + l'ancienne à reprendre.
  const [exchanges, setExchanges] = useState([]);
  const [exchangeSel, setExchangeSel] = useState(null);   // échange ouvert dans le wizard
  const loadExchanges = useCallback(async () => {
    try {
      const params = { ...(isAdmin && asLivreur ? { id_livreur: Number(asLivreur) } : {}) };
      const r = await axios.get(`${domain}/api/mobile-delivery/mine/echanges-a-livrer`, { params, headers });
      setExchanges(Array.isArray(r.data) ? r.data : []);
      setError('');   // succès : lève un éventuel bandeau resté d'un autre onglet
    } catch (e) {
      // Une panne n'est pas « aucun échange à livrer ». Le livreur pourrait
      // rentrer en croyant sa tournée finie. L'écran a déjà un bandeau
      // d'erreur (`setError`) : on s'en sert au lieu de vider en silence.
      setExchanges([]);
      setError(messageErreur(e, 'les échanges à livrer'));
    }
  }, [domain, headers, isAdmin, asLivreur]);
  useEffect(() => { if (scope === 'echanges') loadExchanges(); }, [scope, loadExchanges]);
  // Changer d'onglet lève un bandeau d'erreur resté d'une commande qu'on ne
  // pouvait pas ouvrir (ex. lien profond périmé) : il n'a rien à voir avec le
  // nouvel onglet et bloquait la lecture.
  useEffect(() => { setError(''); setExchangeSel(null); }, [scope]);

  // Exécution de l'échange : la confirmation est désormais portée par le wizard
  // (étape « Valider »), plus de popup. Réutilise le même endpoint prouvé.
  const submitExchange = useCallback(async () => {
    if (!exchangeSel) return;
    setSaving(true);
    try {
      await axios.put(`${domain}/api/mobile-delivery/mine/echange/${exchangeSel.id_detail_livraison}/executer`, { ...targetParam() }, { headers });
      setExchangeSel(null);
      await loadExchanges();
    } catch (e) {
      setError(e.response?.data?.error || 'Exécution impossible.');
    } finally {
      setSaving(false);
    }
  }, [domain, headers, targetParam, loadExchanges, exchangeSel]);

  // V2 « la cliente garde l'ancienne paire » : au lieu d'exécuter l'échange, la cliente
  // renonce et achète l'ancienne. Le serveur annule l'échange (neuve rapportée au magasin,
  // AUCUN décrément) et vend l'ancienne ; l'encaissement est borné au dû côté serveur.
  const submitKeepOriginal = useCallback(async ({ payment, comment, confirmationReprise }) => {
    if (!exchangeSel) return;
    setSaving(true);
    try {
      await axios.put(
        `${domain}/api/mobile-delivery/mine/echange/${exchangeSel.id_detail_livraison}/garder-ancienne`,
        {
          payment: Math.max(0, Number(payment) || 0),
          comment: comment || null,
          confirmation_nouvelle_paire_reprise: confirmationReprise === true,
          ...targetParam(),
        },
        { headers },
      );
      setExchangeSel(null);
      await loadExchanges();
    } catch (e) {
      setError(e.response?.data?.error || 'Opération impossible.');
    } finally {
      setSaving(false);
    }
  }, [domain, headers, targetParam, loadExchanges, exchangeSel]);

  // Rafraîchissement SILENCIEUX (sans spinner) : les nouvelles missions et les
  // échanges à livrer apparaissent d'eux-mêmes — le livreur n'a plus à recharger.
  // Toutes les 45 s tant que l'app est visible, et immédiatement au retour sur l'app.
  const silentRefresh = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (isAdmin && !asLivreur) return;
    try {
      const params = { scope, ...(isAdmin && asLivreur ? { id_livreur: Number(asLivreur) } : {}) };
      const r = await axios.get(`${domain}/api/mobile-delivery/mine`, { params, headers });
      setOrders(r.data.orders || []);
    } catch (e) { /* silencieux : pas d'erreur bloquante sur un rafraîchissement de fond */ }
    if (scope === 'echanges') loadExchanges();
  }, [domain, headers, scope, isAdmin, asLivreur, loadExchanges]);

  useEffect(() => {
    const iv = setInterval(silentRefresh, 45000);
    const onVisible = () => { if (document.visibilityState === 'visible') silentRefresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', silentRefresh);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', silentRefresh);
    };
  }, [silentRefresh]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${domain}/api/mobile-delivery/livreurs`, { headers })
      .then((r) => setLivreurs(r.data.livreurs || []))
      .catch((e) => { setLivreurs([]); setErreurLivreurs(messageErreur(e, 'la liste des livreurs')); });
  }, [domain, headers, isAdmin]);

  // Clé de brouillon propre à la commande (et au livreur supervisé pour l'admin).
  const draftKeyFor = useCallback(
    (idCommande) => draftKey('delivery', `${asLivreur || 'me'}:${idCommande}`),
    [asLivreur],
  );

  // Charge le détail d'une commande et RÉAPPLIQUE le brouillon sauvegardé
  // (paires/motifs/résultats/commentaire/encaissement) : aucune saisie perdue.
  const fetchOrder = useCallback(async (idCommande) => {
    try {
      const response = await axios.get(`${domain}/api/mobile-delivery/mine/${idCommande}`, { params: targetParam(), headers });
      const value = response.data.order;
      const fresh = value.lignes.map((line) => ({
        ...line,
        taken: line.actionnable_prise_en_charge ? line.statut_livraison === 'AFFECTEE' : false,
        reason: 'INDISPONIBLE_AU_DEPART',
        outcome: '',
      }));
      const saved = readDraft(window.localStorage, draftKeyFor(idCommande));
      setOrder(value);
      setLines(mergeLinesWithDraft(fresh, saved));
      setComment(saved?.comment || '');
      setPayment(saved?.payment ?? '');
      // L'horodatage repart TOUJOURS à « maintenant » : l'antidatage est une
      // action explicite (champ Détails du récap), jamais un état repris d'un
      // brouillon d'hier — sinon une réouverture le lendemain enverrait en
      // silence la date de la veille (mouvement stock/vente mal daté).
      setEffectiveAt(localDateTime());
      setDraftRestored(draftHasContent(saved));
      setError('');
    } catch (e) {
      // Une commande impossible à ouvrir vient presque toujours d'un LIEN
      // PÉRIMÉ (notification tapée après réattribution/résolution) : on retire
      // le paramètre `commande` de l'URL pour qu'il ne re-déclenche pas l'échec
      // en boucle, et le reste du hub (missions, échanges) reste utilisable.
      setOrder(null);
      // Forme fonctionnelle : on lit le `prev` frais fourni par react-router au
      // lieu de capturer `searchParams` (évite un closure périmé et le garde
      // hors des deps de fetchOrder).
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('commande');
        return next;
      }, { replace: true });
      setError(e.response?.data?.error || 'Impossible d’ouvrir cette commande.');
    }
  }, [domain, headers, targetParam, draftKeyFor, setSearchParams]);

  // --- Réactivation (Option B) ---------------------------------------------
  // Supervision admin : livraisons OUVERTES en retard (SLA dépassé), tous
  // livreurs confondus. Chargé dès l'arrivée (badge de l'onglet) puis à
  // l'ouverture de l'onglet « En retard ».
  const loadLate = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await axios.get(`${domain}/api/mobile-delivery/admin/en-retard`, { headers });
      setLateList(Array.isArray(r.data.lignes) ? r.data.lignes : []);
      if (r.data.sla) setSla(r.data.sla);
    } catch (e) {
      // Même raison : « aucun retard » et « je n'ai pas pu demander » ne se
      // ressemblent qu'à l'écran.
      setLateList([]);
      setError(messageErreur(e, 'les livraisons en retard'));
    }
  }, [domain, headers, isAdmin]);
  useEffect(() => { if (isAdmin) loadLate(); }, [isAdmin, loadLate]);
  useEffect(() => { if (scope === 'en-retard') loadLate(); }, [scope, loadLate]);

  // R3 — hub livreur VIVANT : à l'arrivée d'un événement « livraison » (affectation,
  // réactivation, réassignation… posé par un AUTRE poste — les actions du livreur
  // lui-même sont masquées côté serveur), on rafraîchit la liste du scope courant
  // (débounce anti-rafale). Flux SSE partagé avec la cloche (une seule connexion).
  // refreshRef réassigné à chaque render → vise toujours le bon scope/loader.
  const refreshRef = useRef(null);
  // Rafraîchissement SILENCIEUX : silentRefresh ne touche ni le spinner ni la
  // bannière d'erreur (couvre orders + échanges) ; en-retard = loadLate (silencieux
  // aussi). On évite ainsi le flash « Chargement… » plein écran à chaque événement.
  refreshRef.current = () => (scope === 'en-retard' ? loadLate() : silentRefresh());
  useEffect(() => {
    let debounce = null;
    const unsub = subscribeRealtime((payload) => {
      const touche = (payload?.notifications || []).some((n) => n.famille === 'livraisons');
      if (!touche) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => { if (refreshRef.current) refreshRef.current(); }, 700);
    });
    return () => { clearTimeout(debounce); unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rouvre la fenêtre d'action d'une livraison en retard (+N h). Rafraîchit la
  // liste de supervision et, si une commande est ouverte, son détail.
  const reactivate = useCallback(async (idDetailLivraison) => {
    setReactivatingId(idDetailLivraison);
    setError('');
    try {
      await axios.put(`${domain}/api/mobile-delivery/admin/reactiver/${idDetailLivraison}`, {}, { headers });
      await loadLate();
      if (commandeParam) await fetchOrder(commandeParam);
    } catch (e) {
      setError(e.response?.data?.error || 'Réactivation impossible.');
    } finally {
      setReactivatingId(null);
    }
  }, [domain, headers, loadLate, commandeParam, fetchOrder]);

  // Ouvre le formulaire de retour PRÉ-REMPLI (client + ligne vendue) depuis une
  // livraison terminée — évite les retours ressaisis « à côté » et non tracés.
  const recordReturn = useCallback((order, line) => {
    navigate(`/retourForm?id_client=${order.client.id}&id_detail_commande=${line.id_detail_commande}`);
  }, [navigate]);

  // D42 — Retour après échange livré. Si la paire revient DÉFECTUEUSE (cas le
  // plus fréquent de ce retour), cocher la case l'envoie en avariées (mvt 11)
  // au lieu de la remettre au stock vendable.
  const recordExchangeReturn = useCallback((order, line) => {
    const label = `${line.nom_produit || ''}${line.taille ? ` P.${line.taille}` : ''}${line.couleur ? ` · ${line.couleur}` : ''}`.trim();
    setConfirmDialog({
      message: `Confirmer le retour de « ${label} » par ${order.client.nom} après échange ?`,
      checkboxLabel: 'Paire défectueuse — mettre en avarie (hors stock vendable)',
      checked: true,
      onConfirm: async (defectueuse) => {
        setError('');
        try {
          await axios.put(`${domain}/api/mobile-delivery/admin/echange/${line.id_detail_livraison}/retour`,
            { avarie: !!defectueuse }, { headers });
          await loadOrders();
        } catch (e) {
          setError(e.response?.data?.error || 'Retour impossible.');
        }
      },
    });
  }, [domain, headers, loadOrders]);

  const openOrderCard = (item) => {
    const next = new URLSearchParams(searchParams);
    next.set('commande', String(item.id_commande));
    setSearchParams(next); // pousse une entrée d'historique -> retour navigateur = liste
  };

  const closeOrder = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('commande');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Ouvre / ferme la commande selon l'URL (retour navigateur, reload, lien direct).
  useEffect(() => {
    if (!commandeParam) { setOrder(null); setDraftRestored(false); return; }
    fetchOrder(commandeParam);
  }, [commandeParam, fetchOrder]);

  // « Repartir de zéro » : jette le brouillon restauré et recharge la commande
  // fraîche (le wizard l'expose quand une saisie précédente a été restaurée).
  const clearCurrentDraft = () => {
    if (commandeParam) clearDraft(window.localStorage, draftKeyFor(commandeParam));
    setDraftRestored(false);
    if (commandeParam) fetchOrder(commandeParam);
  };

  const patchLine = (id, patch) => setLines((current) => current.map((line) => line.id_detail_livraison === id ? { ...line, ...patch } : line));
  const pendingLines = lines.filter((line) => line.actionnable_prise_en_charge);
  const activeLines = lines.filter((line) => line.actionnable_resultat);
  const setAll = (taken) => setLines((current) => current.map((line) => line.actionnable_prise_en_charge ? { ...line, taken } : line));

  // Encaissement : dette calculée sur les paires actuellement marquées « Livrée » de cette commande.
  const deliveredLines = activeLines.filter((line) => line.outcome === 'LIVREE');
  const totalDue = deliveredLines.reduce((sum, line) => sum + Number(line.quantite || 0) * Number(line.prix || 0), 0);
  // Dépôt à l'essai : un ACOMPTE optionnel peut être encaissé au dépôt (Mpesa /
  // virement / espèces), borné à la valeur des paires déposées. Le paiement saisi
  // s'impute d'abord au dû des paires livrées, le reliquat devient l'acompte.
  const trialLines = activeLines.filter((line) => line.outcome === 'EN_ESSAI');
  const trialValue = trialLines.reduce((sum, line) => sum + Number(line.quantite || 0) * Number(line.prix || 0), 0);
  // Acompte déjà versé lors d'un dépôt à l'essai précédent (trop-perçu de la dette) :
  // il couvre d'abord le dû à la résolution, et borne un nouvel acompte (anti-cumul).
  const acompteDisponible = Math.max(0, Number(order?.acompte_disponible) || 0);
  // L'acompte couvre D'ABORD le dû livré ; seul son RÉSIDUEL borne un nouvel acompte
  // (anti-cumul, miroir du serveur qui recalcule le trop-perçu après la vente).
  const acompteResiduel = Math.max(0, acompteDisponible - totalDue);
  const paymentCap = Math.max(0, totalDue - acompteDisponible) + Math.max(0, trialValue - acompteResiduel);
  // Montant saisi borné ; 0 si le champ est vide.
  const paymentAmount = Math.min(Math.max(Number(payment) || 0, 0), paymentCap);
  // Répartition (miroir du serveur) : d'abord le dû des paires livrées, le reliquat
  // devient l'acompte des paires à l'essai.
  const versVentes = Math.min(paymentAmount, Math.max(0, totalDue - acompteDisponible));
  const acompteEssai = Math.max(0, paymentAmount - versVentes);
  const balanceDue = Math.max(totalDue - acompteDisponible - versVentes, 0);

  // Si le plafond baisse (ex. une paire re-bascule de LIVREE/essai vers retour), on
  // re-borne le champ montant pour qu'il n'affiche jamais plus que le max.
  useEffect(() => {
    if (payment === '') return;
    const n = Number(payment);
    if (Number.isFinite(n) && n > paymentCap) setPayment(String(paymentCap));
  }, [paymentCap, payment]);

  const changePayment = (raw) => {
    if (raw === '') return setPayment('');
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return;
    // Clamp : jamais plus que le dû livré + la valeur des paires à l'essai.
    setPayment(String(Math.min(Math.max(numeric, 0), paymentCap)));
  };

  // Sauvegarde continue du brouillon tant qu'une commande est ouverte : la
  // saisie survit au retour en arrière, au changement d'écran et au reload.
  useEffect(() => {
    if (!commandeParam || !order) return;
    writeDraft(window.localStorage, draftKeyFor(commandeParam), {
      comment,
      payment,
      effectiveAt,
      lines: lines.map((l) => ({ id_detail_livraison: l.id_detail_livraison, taken: l.taken, reason: l.reason, outcome: l.outcome })),
    });
  }, [commandeParam, order, lines, comment, payment, effectiveAt, draftKeyFor]);

  const submitTakeCharge = async () => {
    if (!pendingLines.length) return setError('Aucune paire en attente de prise en charge.');
    setSaving(true);
    try {
      await axios.put(`${domain}/api/mobile-delivery/mine/${order.id_commande}/take-charge`, {
        effective_at: effectiveAt,
        comment,
        lines: pendingLines.map((line) => ({ id_detail_livraison: line.id_detail_livraison, taken: line.taken, reason: line.reason })),
        ...targetParam(),
      }, { headers });
      clearDraft(window.localStorage, draftKeyFor(order.id_commande));
      // Wizard : on RECHARGE la même commande (les paires deviennent
      // « à résultat ») au lieu de fermer, pour enchaîner sur l'étape résultat.
      await fetchOrder(order.id_commande);
    } catch (e) {
      setError(e.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const doSubmitOutcome = async (selected) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${domain}/api/mobile-delivery/mine/${order.id_commande}/result`, {
        effective_at: effectiveAt,
        comment,
        payment: paymentAmount,
        lines: selected.map((line) => ({ id_detail_livraison: line.id_detail_livraison, outcome: line.outcome })),
        ...targetParam(),
      }, { headers });
      clearDraft(window.localStorage, draftKeyFor(order.id_commande));
      // ACCUSÉ DE RÉCEPTION : on n'escamote plus l'écran. Le serveur a relu la
      // base après COMMIT (`verifie_en_base`) — on montre ce qui est réellement
      // enregistré, puis le livreur ferme lui-même.
      setConfirmation({ ...(data?.result || {}), _cliente: order.client?.nom || '' });
      await loadOrders();
    } catch (e) {
      // FILET RÉSEAU : le commit a pu réussir et la RÉPONSE se perdre (WebView,
      // réseau qui saute). Avant de crier à l'échec, on relit la commande : si
      // les paires portent déjà leur résultat, c'était enregistré — ne pas
      // pousser le livreur à rejouer.
      let dejaEnregistre = false;
      try {
        const r = await axios.get(`${domain}/api/mobile-delivery/mine/${order.id_commande}`, { params: targetParam(), headers });
        const lignes = r.data?.order?.lignes || [];
        const ids = selected.map((l) => Number(l.id_detail_livraison));
        const concernees = lignes.filter((l) => ids.includes(Number(l.id_detail_livraison)));
        dejaEnregistre = concernees.length > 0 && concernees.every((l) => l.date_resultat || !l.actionnable_resultat);
      } catch (_) { /* relecture impossible : on garde le message d'erreur */ }
      if (dejaEnregistre) {
        clearDraft(window.localStorage, draftKeyFor(order.id_commande));
        setConfirmation({ deja: true, id_commande: order.id_commande, _cliente: order.client?.nom || '' });
        await loadOrders();
      } else {
        setError(e.response?.data?.error || 'Enregistrement impossible.');
      }
    } finally {
      setSaving(false);
    }
  };

  const submitOutcome = async () => {
    const selected = activeLines.filter((line) => line.outcome);
    if (!selected.length) return setError('Choisissez le résultat d’au moins une paire.');
    // ① Montant obligatoire : si au moins une paire est « Livrée et vendue », le montant
    // encaissé doit être renseigné EXPLICITEMENT (0 autorisé = vente à crédit). Sinon on
    // bloque avec un message clair (le champ vide ne doit pas devenir 0 en silence).
    if (deliveredLines.length && String(payment).trim() === '') {
      return setError('Renseignez le montant encaissé — mettez 0 s’il s’agit d’une vente à crédit.');
    }
    // ① Confirmation APRÈS validation : récap encaissé / à crédit / acompte avant d'enregistrer.
    if (deliveredLines.length) {
      const acompteTxt = acompteEssai > 0 ? ` · acompte à l’essai ${money(acompteEssai)}` : '';
      setConfirmDialog({
        message: `Confirmer la vente : ${money(versVentes)} encaissé · ${money(balanceDue)} à crédit${acompteTxt} ?`,
        onConfirm: () => doSubmitOutcome(selected),
      });
      return;
    }
    // Dépôt à l'essai avec acompte : confirmation dédiée (l'acompte reste optionnel —
    // sans montant saisi, le dépôt part directement).
    if (trialLines.length && acompteEssai > 0) {
      setConfirmDialog({
        message: `Confirmer le dépôt à l’essai : ${trialLines.length} paire(s) · acompte ${money(acompteEssai)} encaissé ?`,
        onConfirm: () => doSubmitOutcome(selected),
      });
      return;
    }
    await doSubmitOutcome(selected);
  };

  // --- Corrections admin (réservées au rôle admin côté serveur) ---
  const reassignOrder = async () => {
    if (!reassignTo) return;
    setSaving(true);
    try {
      await axios.put(`${domain}/api/mobile-delivery/admin/reassign`, {
        id_detail_livraisons: lines.map((line) => line.id_detail_livraison),
        id_livreur: Number(reassignTo),
      }, { headers });
      clearDraft(window.localStorage, draftKeyFor(order.id_commande));
      closeOrder();
      setReassignTo('');
      await loadOrders();
    } catch (e) {
      setError(e.response?.data?.error || 'Réassignation impossible.');
    } finally {
      setSaving(false);
    }
  };

  // Récapitulatif « ancien → nouveau » avant d'écrire (arbitrage du propriétaire,
  // 17/07). Cet écran change prix, quantité ET statut d'un coup : le serveur
  // borne chacun (quantité > commandée refusée, prix < 0 refusé) et trace tout,
  // mais aucune borne ne peut voir une faute de frappe sur une valeur valide.
  // On ne liste QUE les champs réellement modifiés — un récap qui répète des
  // valeurs inchangées se fait lire en diagonale, et ne protège plus de rien.
  const demanderConfirmationCorrection = () => {
    if (!correcting) return;
    const ligne = (lines || []).find((l) => Number(l.id_detail_livraison) === Number(correcting));
    const changements = [];
    if (correctForm.prix !== '') {
      changements.push(`prix : ${Number(ligne?.prix ?? 0).toLocaleString('fr-FR')} $ → ${Number(correctForm.prix).toLocaleString('fr-FR')} $`);
    }
    if (correctForm.qte_livre !== '') {
      changements.push(`quantité livrée : ${Number(ligne?.qte_livre ?? 0)} → ${Number(correctForm.qte_livre)}`);
    }
    if (correctForm.statut !== '') {
      changements.push(`statut : ${ligne?.statut_livraison || '—'} → ${correctForm.statut}`);
    }
    if (!changements.length) { setError('Aucune correction saisie.'); return; }
    setConfirmDialog({
      message: `${ligne?.nom_produit || 'Cette paire'} — ${changements.join(' · ')}. Confirmer ?`,
      onConfirm: saveCorrection,
    });
  };

  const saveCorrection = async () => {
    if (!correcting) return;
    const body = {};
    if (correctForm.prix !== '') body.prix = Number(correctForm.prix);
    if (correctForm.qte_livre !== '') body.qte_livre = Number(correctForm.qte_livre);
    if (correctForm.statut !== '') body.statut = correctForm.statut;
    if (!Object.keys(body).length) return setError('Aucune correction saisie.');
    setSaving(true);
    try {
      await axios.put(`${domain}/api/mobile-delivery/admin/line/${correcting}`, body, { headers });
      setCorrecting(null);
      setCorrectForm({ prix: '', qte_livre: '', statut: '' });
      await fetchOrder(order.id_commande);
    } catch (e) {
      setError(e.response?.data?.error || 'Correction impossible.');
    } finally {
      setSaving(false);
    }
  };

  // ② Auto-correction du PRIX par le livreur (sa propre vente). Fenêtre ouverte si la
  // vente a moins de `correctionH` heures ; l'admin peut toujours (fenêtre plus large,
  // gérée côté serveur). Le back refuse un livreur hors fenêtre / une autre vente.
  const correctionOuverte = (l) => {
    if (!l || l.statut_livraison !== 'LIVREE') return false;
    if (isAdmin) return true;
    if (!l.date_vente) return false;
    const ageH = (Date.now() - new Date(l.date_vente).getTime()) / 3_600_000;
    return Number.isFinite(ageH) && ageH <= (Number(correctionH) || 0);
  };
  // Récapitulatif « ancien → nouveau » avant d'écrire (arbitrage du propriétaire,
  // 17/07). Le serveur borne déjà tout (prix négatif refusé, fenêtre de
  // correction vérifiée) et chaque correction est tracée et re-corrigeable :
  // ce récap ne sert PAS à protéger l'intégrité, il sert à attraper la seule
  // erreur qu'aucune borne technique ne peut voir — la faute de frappe sur une
  // valeur valide (150 saisi 15). D'où un récap qui montre les DEUX valeurs
  // plutôt qu'un « Êtes-vous sûr ? », qui ne ferait perdre qu'un geste.
  const demanderConfirmationPrix = () => {
    if (!priceEdit) return;
    const p = Number(priceEdit.prix);
    if (!Number.isFinite(p) || p < 0) { setError('Prix invalide.'); return; }
    const ancien = Number(priceEdit.ancien || 0);
    if (p === ancien) { setError('Le prix est inchangé.'); return; }
    setConfirmDialog({
      message: `${priceEdit.article} : le prix passe de ${ancien.toLocaleString('fr-FR')} $ à ${p.toLocaleString('fr-FR')} $. Confirmer ?`,
      onConfirm: saveLivreurPrice,
    });
  };

  const saveLivreurPrice = async () => {
    if (!priceEdit) return;
    const p = Number(priceEdit.prix);
    if (!Number.isFinite(p) || p < 0) { setError('Prix invalide.'); return; }
    setSaving(true);
    try {
      // N'envoyer QUE { prix } : le serveur refuse un changement de statut/qté par un livreur.
      const { data } = await axios.put(`${domain}/api/mobile-delivery/admin/line/${priceEdit.id}`, { prix: p }, { headers });
      setPriceEdit(null);
      await loadOrders();
      // IMPACT ARGENT VISIBLE : le serveur a recalculé la dette (prix propagé à la
      // vente). On l'annonce, sinon la correction est correcte mais invisible —
      // ex. « attendu 90 $ · encaissé 80 $ → crédit 10 $ ».
      const d = data?.result?.dette;
      if (d) {
        setConfirmation({
          correction: true,
          id_commande: priceEdit.id_commande || null,
          _cliente: priceEdit.article || '',
          montant_convenu: d.montant_convenu,
          montant_paye: d.montant_paye,
          solde: d.solde,
          avoir: d.avoir,
        });
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Correction impossible.');
    } finally { setSaving(false); }
  };

  // Boîte de confirmation PARTAGÉE par la vue détail ET la vue liste. Sans ça, la
  // confirmation de vente (déclenchée depuis le DÉTAIL) n'était jamais montée (elle
  // n'existait que dans le return de la liste) → « Enregistrer » ne faisait rien.
  const confirmDialogNode = confirmDialog && (
    <div className="delivery-confirm-overlay" role="dialog" aria-modal="true" onClick={() => setConfirmDialog(null)}>
      <div className="delivery-confirm-box" onClick={(e) => e.stopPropagation()}>
        <p className="delivery-confirm-msg">{confirmDialog.message}</p>
        {confirmDialog.checkboxLabel && (
          <label className="delivery-confirm-msg" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={!!confirmDialog.checked} onChange={(e) => setConfirmDialog((d) => d && { ...d, checked: e.target.checked })} />
            {confirmDialog.checkboxLabel}
          </label>
        )}
        <div className="delivery-confirm-actions">
          <button type="button" className="delivery-confirm-cancel" onClick={() => setConfirmDialog(null)}>Annuler</button>
          <button type="button" className="delivery-confirm-ok" onClick={() => { const fn = confirmDialog.onConfirm; const coche = !!confirmDialog.checked; setConfirmDialog(null); if (fn) fn(coche); }}>Confirmer</button>
        </div>
      </div>
    </div>
  );

  if (scope === 'echanges' && exchangeSel) {
    return (
      <main className="mobile-delivery-hub mobile-delivery-hub--detail">
        <ExchangeWizard
          exchange={exchangeSel}
          onClose={() => setExchangeSel(null)}
          onSubmit={submitExchange}
          onKeepOriginal={submitKeepOriginal}
          saving={saving}
          error={error}
          clearError={() => setError('')}
          normalizePhoneCd={normalizePhoneCd}
        />
        {confirmDialogNode}
      </main>
    );
  }

  // ── ACCUSÉ DE RÉCEPTION — après enregistrement RELU EN BASE par le serveur ──
  // Remplace le wizard : le livreur voit ce qui a réellement été écrit, puis ferme.
  if (confirmation) {
    const c = confirmation;
    // Une correction de prix ne quitte pas la commande : on referme juste l'accusé.
    const fermer = () => { setConfirmation(null); if (!c.correction) closeOrder(); };
    if (c.correction) {
      return (
        <main className="mobile-delivery-hub mobile-delivery-hub--detail">
          <section className="dlv-ack">
            <div className="dlv-ack__ic" aria-hidden="true">✓</div>
            <h2 className="dlv-ack__t">Prix corrigé</h2>
            <p className="dlv-ack__sub">{c._cliente || 'Article'} — la créance a été recalculée.</p>
            <span className="dlv-ack__badge">✓ Recalculé en base</span>
            <div className="dlv-ack__money">
              <div><span>Nouveau montant attendu</span><b>{money(c.montant_convenu)}</b></div>
              <div><span>Déjà encaissé</span><b className="ok">{money(c.montant_paye)}</b></div>
              {Number(c.solde) > 0 && <div><span>Crédit restant</span><b className="due">{money(c.solde)}</b></div>}
              {Number(c.avoir) > 0 && <div><span>Trop-perçu (avoir)</span><b className="ok">{money(c.avoir)}</b></div>}
              {Number(c.solde) === 0 && Number(c.avoir) === 0 && (
                <div><span>Solde</span><b className="ok">Soldé</b></div>
              )}
            </div>
            <button type="button" className="dlv-ack__btn" onClick={fermer}>Terminé</button>
          </section>
        </main>
      );
    }
    return (
      <main className="mobile-delivery-hub mobile-delivery-hub--detail">
        <section className="dlv-ack">
          <div className="dlv-ack__ic" aria-hidden="true">✓</div>
          <h2 className="dlv-ack__t">{c.deja ? 'C’était déjà enregistré' : 'Livraison enregistrée'}</h2>
          <p className="dlv-ack__sub">
            {c.deja
              ? `La commande #${c.id_commande} était déjà validée — rien n’a été enregistré en double.`
              : `Commande #${c.id_commande}${c._cliente ? ` · ${c._cliente}` : ''}`}
          </p>
          {!c.deja && (
            <>
              {c.verifie_en_base && <span className="dlv-ack__badge">✓ Vérifié en base</span>}
              {Array.isArray(c.lignes) && c.lignes.length > 0 && (
                <div className="dlv-ack__lines">
                  {c.lignes.map((l) => (
                    <div className="dlv-ack__line" key={l.id_detail_livraison}>
                      <span className="dlv-ack__pn">
                        {l.nom_produit}{l.taille ? ` · P${l.taille}` : ''}
                        {l.reference ? <code className="dlv-ack__ref">{l.reference}</code> : null}
                      </span>
                      <b>{statusLabel(l.statut)}</b>
                    </div>
                  ))}
                </div>
              )}
              <div className="dlv-ack__money">
                {Number(c.montant_encaisse) > 0 && (
                  <div><span>Encaissé maintenant</span><b className="ok">{money(c.montant_encaisse)}</b></div>
                )}
                {Number(c.solde) > 0 && <div><span>Reste dû</span><b className="due">{money(c.solde)}</b></div>}
                {Number(c.ventes_creees) > 0 && <div><span>Vente(s) enregistrée(s)</span><b>{c.ventes_creees}</b></div>}
              </div>
            </>
          )}
          <button type="button" className="dlv-ack__btn" onClick={fermer}>Terminé</button>
        </section>
      </main>
    );
  }

  if (order) {
    return (
      <main className="mobile-delivery-hub mobile-delivery-hub--detail">
        <DeliveryWizard
          order={order}
          patchLine={patchLine}
          pendingLines={pendingLines}
          activeLines={activeLines}
          setAll={setAll}
          payment={payment}
          changePayment={changePayment}
          paymentCap={paymentCap}
          totalDue={totalDue}
          deliveredLines={deliveredLines}
          trialLines={trialLines}
          acompteEssai={acompteEssai}
          balanceDue={balanceDue}
          onTakeCharge={submitTakeCharge}
          onSubmitResult={submitOutcome}
          saving={saving}
          onClose={closeOrder}
          money={money}
          normalizePhoneCd={normalizePhoneCd}
          draftRestored={draftRestored}
          onClearDraft={clearCurrentDraft}
          scope={scope}
          error={error}
          clearError={() => setError('')}
          effectiveAt={effectiveAt}
          setEffectiveAt={setEffectiveAt}
          minEffectiveAt={order?.date_livraison_min || order?.date_commande || null}
          comment={comment}
          setComment={setComment}
          acompteDisponible={acompteDisponible}
        />
      {isAdmin && <section className="mobile-delivery-card delivery-admin-card">
        <div className="delivery-row delivery-section-head"><strong>Outils admin</strong><span className="delivery-count-tag">correction</span></div>
        <label className="delivery-label" htmlFor="delivery-reassign">Réassigner la commande à un autre livreur</label>
        <div className="delivery-admin-row">
          <select id="delivery-reassign" className="delivery-input" value={reassignTo} onChange={(event) => setReassignTo(event.target.value)}>
            <option value="">— Choisir un livreur —</option>
            {livreurs.filter((l) => String(l.id) !== String(asLivreur)).map((l) => <option key={l.id} value={l.id}>{l.username}</option>)}
          </select>
          <button type="button" className="delivery-admin-btn" disabled={!reassignTo || saving} onClick={reassignOrder}>Réassigner</button>
        </div>
        {/* SOUS la rangée, pas dedans : `.delivery-admin-row` est un flex, le
            message y aurait écrasé le menu et le bouton. */}
        {erreurLivreurs && <p className="delivery-error">{erreurLivreurs}</p>}
        <label className="delivery-label" htmlFor="delivery-correct-line">Corriger une paire (prix, quantité, statut)</label>
        <select id="delivery-correct-line" className="delivery-input" value={correcting || ''} onChange={(event) => { setCorrecting(event.target.value ? Number(event.target.value) : null); setCorrectForm({ prix: '', qte_livre: '', statut: '' }); }}>
          <option value="">— Choisir une paire —</option>
          {lines.map((line) => <option key={line.id_detail_livraison} value={line.id_detail_livraison}>{line.nom_produit} · P{line.taille} · #{line.id_detail_livraison}</option>)}
        </select>
        {correcting && <div className="delivery-admin-correct">
          <input className="delivery-input" type="number" min="0" step="0.01" placeholder="Nouveau prix" value={correctForm.prix} onChange={(event) => setCorrectForm((f) => ({ ...f, prix: event.target.value }))} />
          <input className="delivery-input" type="number" min="0" step="1" placeholder="Qté livrée" value={correctForm.qte_livre} onChange={(event) => setCorrectForm((f) => ({ ...f, qte_livre: event.target.value }))} />
          <select className="delivery-input" value={correctForm.statut} onChange={(event) => setCorrectForm((f) => ({ ...f, statut: event.target.value }))}>
            <option value="">Statut (inchangé)</option>
            <option value="AFFECTEE">Ré-ouvrir (À prendre)</option>
            <option value="PRISE_EN_CHARGE">Prise en charge</option>
            <option value="EN_COURS">En cours</option>
            <option value="REPORTEE">Reportée</option>
            <option value="NON_PRISE_EN_CHARGE">Non prise</option>
          </select>
          <button type="button" className="delivery-admin-btn" disabled={saving} onClick={demanderConfirmationCorrection}>Enregistrer la correction</button>
        </div>}
        <label className="delivery-label">Clôturer la commande (solde le reste)</label>
        <button type="button" className="delivery-admin-btn delivery-cloture-btn" disabled={saving}
          onClick={() => setConfirmDialog({
            message: 'Clôturer : les paires non livrées reviennent au magasin. Avec au moins une vente la commande reste « Livrée » ; sans aucune vente elle passe « Annulée ». Continuer ?',
            onConfirm: async () => {
              try {
                setSaving(true);
                await axios.put(`${domain}/api/commande/cloturer/${order.id_commande}`, {}, { headers });
                closeOrder();
                await loadOrders();
              } catch (e) {
                setError(e.response?.data?.error || 'Clôture impossible.');
              } finally { setSaving(false); }
            },
          })}>
          Clôturer la commande
        </button>
        {error && <div className="delivery-error" role="alert"><span>{error}</span></div>}
      </section>}
      {confirmDialogNode}
    </main>
    );
  }

  return <main className="mobile-delivery-hub">
    <header className="mobile-delivery-head">
      <div className="mobile-delivery-head-top">
        <div>
          <span className="delivery-eyebrow">Ndoé livraisons</span>
          <p className="delivery-hello">Bonjour {user?.username || 'livreur'}</p>
          <h1>Mes livraisons</h1>
        </div>
        <button type="button" className="delivery-refresh" onClick={scope === 'en-retard' ? loadLate : scope === 'echanges' ? loadExchanges : loadOrders} disabled={loading} aria-label="Actualiser mes livraisons">↻</button>
      </div>
      <span className="delivery-head-caption">Confirmez les paires réellement reçues puis leur résultat.</span>
      <div className="delivery-head-stats">
        {scope === 'en-retard'
          ? <span className="delivery-chip"><b>{lateList.length}</b> livraison(s) en retard</span>
          : scope === 'closed'
          ? <span className="delivery-chip"><b>{orders.length}</b> livraison(s) terminée(s)</span>
          : <>
              <span className="delivery-chip"><b>{orders.length}</b> commande(s)</span>
              <span className="delivery-chip"><b>{orders.reduce((sum, o) => sum + (scope === 'active' ? (o.lignes_en_cours || 0) : (o.lignes_a_prendre || 0)), 0)}</b> ligne(s) à traiter</span>
            </>}
      </div>
      {isAdmin && <div className="delivery-admin-picker">
        <label htmlFor="delivery-livreur">Superviser le livreur</label>
        <select id="delivery-livreur" className="delivery-input" value={asLivreur} onChange={(event) => { setAsLivreur(event.target.value); closeOrder(); }}>
          <option value="">— Choisir un livreur —</option>
          {livreurs.map((l) => <option key={l.id} value={l.id}>{l.username}</option>)}
        </select>
        {erreurLivreurs && <p className="delivery-error">{erreurLivreurs}</p>}
      </div>}
    </header>
    <div className="delivery-tabs" role="tablist"><button type="button" role="tab" aria-selected={scope === 'take-charge'} className={scope === 'take-charge' ? 'is-active' : ''} onClick={() => setScope('take-charge')}>À prendre</button><button type="button" role="tab" aria-selected={scope === 'active'} className={scope === 'active' ? 'is-active' : ''} onClick={() => setScope('active')}>En circulation</button><button type="button" role="tab" aria-selected={scope === 'echanges'} className={scope === 'echanges' ? 'is-active' : ''} onClick={() => setScope('echanges')}>Échanges à livrer{exchanges.length ? ` (${exchanges.length})` : ''}</button>{isAdmin && <button type="button" role="tab" aria-selected={scope === 'en-retard'} className={scope === 'en-retard' ? 'is-active' : ''} onClick={() => setScope('en-retard')}>⏰ En retard{lateList.length ? ` (${lateList.length})` : ''}</button>}<button type="button" role="tab" aria-selected={scope === 'closed'} className={scope === 'closed' ? 'is-active' : ''} onClick={() => setScope('closed')}>Livrées</button></div>
    {error && <div className="delivery-error" role="alert"><span>{error}</span><button type="button" className="delivery-retry" onClick={scope === 'en-retard' ? loadLate : scope === 'echanges' ? loadExchanges : loadOrders}>Réessayer</button></div>}
    {scope === 'en-retard'
      ? (lateList.length === 0
          ? <div className="delivery-empty"><span className="delivery-empty-badge" aria-hidden="true">✓</span><strong>Aucune livraison en retard</strong><p>Les livraisons non traitées au-delà de {sla.jours} jours apparaîtront ici, prêtes à être réactivées.</p></div>
          : <div className="delivery-order-list">
              <p className="delivery-late-intro">Non traitées depuis plus de {sla.jours} jours (tous livreurs). Réactiver rouvre une fenêtre de {sla.relance_heures} h pour le livreur.</p>
              {lateList.map((l) => (
                <div key={l.id_detail_livraison} className="delivery-order-card delivery-late-card" style={{ cursor: 'default' }}>
                  <div className="delivery-row"><strong>{l.client?.nom || '—'}</strong><span className="delivery-late-chip">⏰ {l.age_jours} j</span></div>
                  <p className="delivery-address">{l.nom_produit} · P{l.taille ?? '?'}{l.couleur ? ` · ${l.couleur}` : ''} · Qté {l.quantite}</p>
                  <small className="delivery-order-meta">Livreur : {l.livreur || `#${l.id_livreur}`} · Commande #{l.id_commande} · {statusLabel(l.statut_livraison)}</small>
                  <button type="button" className="delivery-submit delivery-reactivate-btn" disabled={reactivatingId === l.id_detail_livraison} onClick={() => reactivate(l.id_detail_livraison)}>{reactivatingId === l.id_detail_livraison ? 'Réactivation…' : `Réactiver — ${sla.relance_heures} h`}</button>
                </div>))}
            </div>)
      : scope === 'echanges'
      ? (exchanges.length === 0
          ? <div className="delivery-empty"><span className="delivery-empty-badge" aria-hidden="true">✦</span><strong>Aucun échange à livrer</strong><p>Les échanges préparés par le magasin apparaîtront ici : remettre la nouvelle paire, reprendre l'ancienne.</p></div>
          : <div className="delivery-order-list">{exchanges.map((x) => (
              <div key={x.id_detail_livraison} className="delivery-order-card" style={{ cursor: 'pointer' }} onClick={() => setExchangeSel(x)}>
                <div className="delivery-row"><strong>{x.nom_client}</strong><span className="delivery-pairs-tag">Échange</span></div>
                <p className="delivery-address">Remettre : <b>{x.produit_neuf} · P.{x.taille_neuf ?? '?'}{x.couleur_neuf ? ` · ${x.couleur_neuf}` : ''}</b></p>
                <p className="delivery-address">Reprendre : {x.produit_ancien || '—'}{x.taille_ancien ? ` · P.${x.taille_ancien}` : ''}</p>
                {Number(x.avarie_ancienne) === 1 && <span className="delivery-late-chip delivery-late-chip--card">⚠ Ancienne défectueuse → avarie</span>}
                <button type="button" className="delivery-submit" onClick={(e) => { e.stopPropagation(); setExchangeSel(x); }}>Ouvrir l’échange →</button>
              </div>))}</div>)
      : scope === 'closed'
      ? (loading
          ? <div className="delivery-loading" aria-busy="true"><span className="delivery-spinner" aria-hidden="true" /><p>Chargement des livraisons terminées…</p></div>
          : orders.length === 0
            ? <div className="delivery-empty"><span className="delivery-empty-badge" aria-hidden="true">✓</span><strong>{isAdmin && !asLivreur ? 'Choisissez un livreur' : 'Aucune livraison terminée'}</strong><p>{isAdmin && !asLivreur ? 'Sélectionnez un livreur pour voir ses livraisons terminées et enregistrer un retour.' : 'Les livraisons terminées apparaîtront ici.'}</p></div>
            : <div className="delivery-order-list">{orders.map((o) => (
                <div key={o.id_commande} className="delivery-order-card" style={{ cursor: 'default' }}>
                  <div className="delivery-row"><strong>{o.client.nom}</strong><span className={`delivery-pairs-tag${pairsProgress(o).cls}`}>{pairsProgress(o).text}</span></div>
                  <p className="delivery-address">{o.client.adresse}</p>
                  {o.client?.telephone && <a className="delivery-wa delivery-wa--sm" href={buildWaLink(o.client.telephone)} target="_blank" rel="noopener noreferrer" aria-label={`Écrire à ${o.client.nom} sur WhatsApp`}>💬 WhatsApp</a>}
                  <small className="delivery-order-meta">Commande #{o.id_commande}</small>
                  {/* Encaissé (vert) en regard de l'attendu (encre) : le livreur voit
                      d'un coup d'œil ce qui reste dû sur une livraison terminée. */}
                  {(() => {
                    const attendu = (o.lignes || [])
                      .filter((l) => l.statut_livraison === 'LIVREE')
                      .reduce((s, l) => s + (Number(l.prix) || 0) * (Number(l.quantite) || 0), 0);
                    const enc = Number(o.montant_encaisse) || 0;
                    if (attendu <= 0 && enc <= 0) return null;
                    const reste = Math.max(0, attendu - enc);
                    return (
                      <div className="delivery-cash">
                        <b className={reste === 0 ? 'is-full' : 'is-part'}>{money(enc)}</b>
                        <span>/ {money(attendu)} attendu</span>
                        {reste > 0 && <em>reste {money(reste)}</em>}
                      </div>
                    );
                  })()}
                  <div className="delivery-closed-lines">
                    {o.lignes.map((l) => (
                      <div key={l.id_detail_livraison} className="delivery-closed-line">
                        <span className="delivery-closed-line-label">{l.nom_produit}{l.reference ? ` · ${l.reference}` : ''} · P{l.taille}{l.couleur ? ` · ${l.couleur}` : ''} · <b className={`delivery-closed-status${toneClass(l.statut_livraison)}`}>{statusLabel(l.statut_livraison)}</b>{l.statut_livraison === 'LIVREE' && l.prix ? ` · ${money(l.quantite * l.prix)}` : ''}</span>
                        {l.img && <button type="button" className="delivery-img-toggle" aria-expanded={!!imgVisible[l.id_detail_livraison]} onClick={() => toggleImg(l.id_detail_livraison)}>{imgVisible[l.id_detail_livraison] ? '▲ Masquer l’image' : '🖼 Afficher l’image'}</button>}
                        {l.img && imgVisible[l.id_detail_livraison] && <div className="delivery-img-wrap"><NdoeImage src={l.img} alt={l.nom_produit || 'Article'} size="md" /></div>}
                        {isAdmin && l.statut_livraison === 'LIVREE' && <button type="button" className="delivery-return-btn" onClick={() => recordReturn(o, l)}>Enregistrer un retour</button>}
                        {isAdmin && l.statut_livraison === 'ECHANGE_TERMINE' && <button type="button" className="delivery-return-btn" onClick={() => recordExchangeReturn(o, l)}>Retour après échange</button>}
                        {correctionOuverte(l) && (priceEdit?.id === l.id_detail_livraison
                          ? <span className="delivery-price-edit">
                              <input type="number" min="0" step="0.01" value={priceEdit.prix} onChange={(e) => setPriceEdit((pe) => ({ ...pe, prix: e.target.value }))} />
                              <button type="button" disabled={saving} onClick={demanderConfirmationPrix}>Valider</button>
                              <button type="button" onClick={() => setPriceEdit(null)}>Annuler</button>
                            </span>
                          : <button type="button" className="delivery-return-btn" onClick={() => setPriceEdit({ id: l.id_detail_livraison, prix: String(l.prix ?? ''), ancien: Number(l.prix ?? 0), article: l.nom_produit || 'cet article' })}>Corriger le prix{!isAdmin ? ` (sous ${correctionH} h)` : ''}</button>)}
                      </div>))}
                  </div>
                </div>))}</div>)
      : (loading
        ? <div className="delivery-loading" aria-busy="true"><span className="delivery-spinner" aria-hidden="true" /><p>Chargement de vos livraisons…</p></div>
        : orders.length === 0
          ? <div className="delivery-empty"><span className="delivery-empty-badge" aria-hidden="true">✦</span><strong>{isAdmin && !asLivreur ? 'Choisissez un livreur' : (scope === 'active' ? 'Aucune paire en circulation' : 'Aucune mission à prendre')}</strong><p>{isAdmin && !asLivreur ? 'Sélectionnez un livreur ci-dessus pour voir et opérer ses livraisons.' : (scope === 'active' ? 'Les livraisons en cours apparaîtront ici.' : 'Les commandes qui vous sont affectées apparaîtront ici.')}</p></div>
          : <div className="delivery-order-list">{orders.map((item) => {
              // La carte = zone tapable (ouvre le détail) + rangée d'actions.
              // « Appeler » réutilise le téléphone déjà servi par /mine (aucun
              // appel/route en plus) ; le CTA explicite double le tap de la carte.
              const tel = normalizePhoneCd(item.client?.telephone || '');
              const cta = scope === 'active' ? 'Suivre la livraison' : 'Prendre en charge';
              const ouvrir = () => openOrderCard(item);
              return (
                <div className="delivery-order-card delivery-card--stack" key={item.id_commande}>
                  {/* role=button SANS aria-label : le nom accessible vient du
                      contenu (cliente + progression + adresse + urgence + lignes),
                      parité avec l'ancien <button> ; un aria-label masquerait ces
                      détails (role button = children presentational). */}
                  <div className="delivery-card-open" role="button" tabIndex={0}
                    onClick={ouvrir}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(); } }}>
                    <div className="delivery-row"><strong>{item.client.nom}</strong><span className={`delivery-pairs-tag${pairsProgress(item).cls}`}>{pairsProgress(item).text}</span></div>
                    <p className="delivery-address">{item.client.adresse}</p>
                    {item.lignes_en_retard > 0 && <span className="delivery-late-chip delivery-late-chip--card">⏰ {item.lignes_en_retard} en retard</span>}
                    <small className="delivery-order-meta">Commande #{item.id_commande} · {scope === 'active' ? item.lignes_en_cours : item.lignes_a_prendre} ligne(s) à traiter</small>
                  </div>
                  <div className="delivery-card-actions">
                    {tel && <a className="delivery-call" href={`tel:+${tel}`} onClick={(e) => e.stopPropagation()} aria-label={`Appeler ${item.client.nom}`}>📞 Appeler</a>}
                    {tel && <a className="delivery-wa" href={buildWaLink(item.client?.telephone)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`Écrire à ${item.client.nom} sur WhatsApp`}>💬 WhatsApp</a>}
                    <button type="button" className="delivery-cta" onClick={ouvrir}>{cta} <span aria-hidden="true">→</span></button>
                  </div>
                </div>
              );
            })}</div>)}
  {confirmDialogNode}
  </main>;
}
